import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { GetApp, Refresh } from "@material-ui/icons";
import { useAuth } from "@airborne/react-admin";
import * as React from "react";
import { useNotify } from "react-admin";
import { useAppDataProvider } from "../../../dataprovider";
import { AircraftScoreModel, AiReliabilityReportDataModel, CustomerScoreModel } from "../Model/AiReliabilityModels";
import AircraftScoresTable from "./AircraftScoresTable";
import { buildAiReliabilityPdfHtml } from "./AiReliabilityPdfReport";
import CustomerScoreCard from "./CustomerScoreCard";

interface CustomerOption {
  ownerCode: string;
  customerName: string;
}

const AiReliabilitySection: React.FC = () => {
  const { user } = useAuth();
  const dataProvider = useAppDataProvider();
  const notify = useNotify();

  const [customerList, setCustomerList] = React.useState<CustomerOption[]>([]);
  const [selectedOwnerCode, setSelectedOwnerCode] = React.useState<string>("");
  const [customerScore, setCustomerScore] = React.useState<CustomerScoreModel | null>(null);
  const [aircraftScores, setAircraftScores] = React.useState<AircraftScoreModel[]>([]);
  const [loadingCustomers, setLoadingCustomers] = React.useState(false);
  const [loadingScore, setLoadingScore] = React.useState(false);
  const [loadingAircrafts, setLoadingAircrafts] = React.useState(false);
  const [loadingPdf, setLoadingPdf] = React.useState(false);
  const [loadingCalculate, setLoadingCalculate] = React.useState(false);

  const isAdmin = user?.role === "admin";

  // Load customer list for internal (non-customer) users, or auto-set for customers
  React.useEffect(() => {
    if (user?.isCustomer) {
      if (user?.ownerCode) {
        setSelectedOwnerCode(user.ownerCode);
      }
    } else {
      setLoadingCustomers(true);
      dataProvider
        .getList("customers/", {
          pagination: { page: 1, perPage: 9999 },
          sort: { field: "id", order: "DESC" },
          filter: {},
        })
        .then(response => {
          setCustomerList(response.data as unknown as CustomerOption[]);
        })
        .catch(() => {
          notify("Müşteri listesi yüklenemedi.", "error");
        })
        .finally(() => setLoadingCustomers(false));
    }
  }, []);

  // Fetch scores when ownerCode changes
  React.useEffect(() => {
    if (!selectedOwnerCode) return;

    setLoadingScore(true);
    setLoadingAircrafts(true);
    setCustomerScore(null);
    setAircraftScores([]);

    dataProvider
      .getAiReliabilityCustomerScore(selectedOwnerCode)
      .then(res => {
        setCustomerScore(res?.data ?? null);
      })
      .catch(() => {
        notify("Müşteri skoru yüklenemedi.", "error");
        setCustomerScore(null);
      })
      .finally(() => setLoadingScore(false));

    dataProvider
      .getAiReliabilityAircraftScores(selectedOwnerCode)
      .then(res => {
        setAircraftScores(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        notify("Uçak skorları yüklenemedi.", "error");
        setAircraftScores([]);
      })
      .finally(() => setLoadingAircrafts(false));
  }, [selectedOwnerCode]);

  const handleDownloadPdf = async () => {
    if (!selectedOwnerCode) return;
    setLoadingPdf(true);
    try {
      const res = await dataProvider.getAiReliabilityReportData(selectedOwnerCode);
      const reportData: AiReliabilityReportDataModel = res?.data;
      const html = buildAiReliabilityPdfHtml(reportData, selectedOwnerCode);
      const blobRes = await dataProvider.getMatbaaBlobData(html);
      const data = blobRes?.data;
      if (data instanceof Blob) {
        const pdfBlob = new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(pdfBlob);
        const now = new Date();
        const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const link = document.createElement("a");
        link.href = url;
        link.download = `ai-reliability-${selectedOwnerCode}-${yearMonth}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch {
      notify("PDF oluşturulamadı.", "error");
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleRecalculate = async () => {
    if (!selectedOwnerCode || !isAdmin) return;
    setLoadingCalculate(true);
    try {
      await dataProvider.postAiReliabilityCalculate(selectedOwnerCode);
      notify("Analiz yenileme başlatıldı.", "success");
      // Refresh scores after recalculation
      setLoadingScore(true);
      setLoadingAircrafts(true);
      const [scoreRes, aircraftRes] = await Promise.all([
        dataProvider.getAiReliabilityCustomerScore(selectedOwnerCode),
        dataProvider.getAiReliabilityAircraftScores(selectedOwnerCode),
      ]);
      setCustomerScore(scoreRes?.data ?? null);
      setAircraftScores(Array.isArray(aircraftRes?.data) ? aircraftRes.data : []);
    } catch {
      notify("Analiz yenilenemedi.", "error");
    } finally {
      setLoadingCalculate(false);
      setLoadingScore(false);
      setLoadingAircrafts(false);
    }
  };

  return (
    <Box>
      {/* Header row: Customer Select + action buttons */}
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={2} style={{ gap: 12 }}>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          {/* Customer dropdown — only for internal (non-customer) users */}
          {!user?.isCustomer && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
              <InputLabel id="ai-customer-select-label">Müşteri</InputLabel>
              <Select
                labelId="ai-customer-select-label"
                value={selectedOwnerCode}
                onChange={e => setSelectedOwnerCode(e.target.value as string)}
                label="Müşteri"
                disabled={loadingCustomers}
              >
                <MenuItem value="">
                  <em>Seçiniz...</em>
                </MenuItem>
                {customerList.map(c => (
                  <MenuItem key={c.ownerCode} value={c.ownerCode}>
                    {c.customerName} ({c.ownerCode})
                  </MenuItem>
                ))}
              </Select>
              {loadingCustomers && (
                <Box position="absolute" right={36} top={10}>
                  <CircularProgress size={16} />
                </Box>
              )}
            </FormControl>
          )}

          {/* For customers: show their ownerCode as a label */}
          {user?.isCustomer && selectedOwnerCode && (
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {selectedOwnerCode}
            </Typography>
          )}
        </Box>

        {/* Action buttons */}
        <Box display="flex" style={{ gap: 8 }}>
          {isAdmin && (
            <Button
              variant="outlined"
              size="small"
              startIcon={loadingCalculate ? <CircularProgress size={14} /> : <Refresh />}
              onClick={handleRecalculate}
              disabled={!selectedOwnerCode || loadingCalculate}
            >
              Analizi Yenile
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={loadingPdf ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <GetApp />}
            onClick={handleDownloadPdf}
            disabled={!selectedOwnerCode || loadingPdf}
          >
            PDF İndir
          </Button>
        </Box>
      </Box>

      {/* No customer selected placeholder for internal users */}
      {!user?.isCustomer && !selectedOwnerCode && (
        <Typography variant="body2" color="textSecondary" style={{ padding: "24px 0" }}>
          Analiz görmek için lütfen bir müşteri seçin.
        </Typography>
      )}

      {/* Score content */}
      {selectedOwnerCode && (
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <CustomerScoreCard score={customerScore} loading={loadingScore} />
          <AircraftScoresTable scores={aircraftScores} loading={loadingAircrafts} />
        </Box>
      )}
    </Box>
  );
};

export default AiReliabilitySection;
